import { useCallback, useRef, useState } from 'react';

import { notifications } from '@mantine/notifications';

import {
  EscalatedSubject,
  EscalationProcessingStatusResponse,
  ProcessingStatus,
  vnextApiClient,
} from '@/services/vnext-api';
import { usePoll } from './usePoll';

/** Escalations are long-running graph jobs; a slow cadence is plenty. */
const ESCALATION_POLL_MS = 10_000;

interface EscalationState {
  threadId: string;
  subject: EscalatedSubject;
  status: ProcessingStatus;
  message: string;
  projectDescription?: string | null;
  worker_threads?: string[] | null;
  error?: string | null;
}

interface UseEscalationStatusState {
  isProcessing: boolean;
  escalations: Map<string, EscalationState>;
  error: Error | null;
}

const isActive = (escalation: EscalationState) =>
  escalation.status === ProcessingStatus.Pending || escalation.status === ProcessingStatus.Running;

export function useEscalationStatus() {
  const [state, setState] = useState<UseEscalationStatusState>({
    isProcessing: false,
    escalations: new Map(),
    error: null,
  });
  // The poll reads the live map through a ref so a status update doesn't
  // restart the cadence.
  const escalationsRef = useRef(state.escalations);
  escalationsRef.current = state.escalations;

  const startEscalations = useCallback((subjects: EscalatedSubject[]) => {
    const newEscalations = new Map<string, EscalationState>();

    for (const subject of subjects) {
      newEscalations.set(subject.thread_id, {
        threadId: subject.thread_id,
        subject,
        status: ProcessingStatus.Pending,
        message: 'Escalation started',
      });
    }

    setState({
      isProcessing: true,
      escalations: newEscalations,
      error: null,
    });
  }, []);

  const checkStatus = useCallback(
    async (
      threadId: string,
      signal?: AbortSignal
    ): Promise<EscalationProcessingStatusResponse | null> => {
      const escalation = escalationsRef.current.get(threadId);
      if (!escalation) {
        return null;
      }

      try {
        const response = await vnextApiClient.checkEscalationStatus(threadId, escalation.subject, {
          signal,
        });

        setState((prev) => {
          const newEscalations = new Map(prev.escalations);
          newEscalations.set(threadId, {
            ...escalation,
            subject: {
              ...escalation.subject,
              worker_threads: response.worker_threads ?? escalation.subject.worker_threads ?? [],
            },
            status: response.status,
            message: response.message,
            projectDescription: response.project_description,
            worker_threads: response.worker_threads,
            error: response.error,
          });

          return {
            ...prev,
            escalations: newEscalations,
          };
        });

        if (response.status === ProcessingStatus.Completed) {
          notifications.show({
            title: 'Escalation Complete',
            message: `Subject "${response.subject_name || escalation.subject.subject_name}" escalated successfully`,
            color: 'green',
          });
        } else if (response.status === ProcessingStatus.Failed) {
          notifications.show({
            title: 'Escalation Failed',
            message: response.error || 'An error occurred during escalation',
            color: 'red',
          });
        }

        return response;
      } catch (error) {
        if (signal?.aborted) {
          return null; // the poll was superseded or the tab hidden — not a failure
        }
        const err = error instanceof Error ? error : new Error('Failed to check escalation status');

        setState((prev) => ({
          ...prev,
          error: err,
        }));

        notifications.show({
          title: 'Status Check Failed',
          message: err.message,
          color: 'red',
        });

        return null;
      }
    },
    []
  );

  const reset = useCallback(() => {
    setState({
      isProcessing: false,
      escalations: new Map(),
      error: null,
    });
  }, []);

  // Poll every active escalation while any is still running. A tick that
  // finds nothing active flips `isProcessing` off.
  usePoll<{ allCompleted: boolean }>({
    enabled: state.isProcessing && state.escalations.size > 0,
    interval: ESCALATION_POLL_MS,
    // The jobs were just started — the first check waits one interval.
    immediate: false,
    queryFn: async ({ signal }) => {
      let allCompleted = true;
      for (const [threadId, escalation] of escalationsRef.current) {
        if (isActive(escalation)) {
          allCompleted = false;
          await checkStatus(threadId, signal);
        }
      }
      return { allCompleted };
    },
    onData: ({ allCompleted }) => {
      if (allCompleted) {
        setState((prev) => ({ ...prev, isProcessing: false }));
      }
    },
  });

  return {
    ...state,
    startEscalations,
    checkStatus,
    reset,
  };
}
